import React, { useState, useEffect, useRef } from 'react';
import { router } from '@inertiajs/react';
import { SettingsLayout } from '../../../design-system/components/layouts';
import {
  PageHeader,
  DataTable,
  Badge,
  Button,
  Input,
  Select,
  Modal,
  ModalTitle,
  ModalBody,
  ModalFooter,
  ConfirmDialog,
  Card,
  CardHeader,
  CardBody,
  Collapsible,
  Toggle,
} from '../../../design-system/components';
import { Link } from '@inertiajs/react';
import { ArrowRight, CheckCircle, XCircle, Wifi, Globe, Server, ExternalLink, Loader2 } from 'lucide-react';
import { admin_settings_admins_path, admin_settings_admin_path, admin_settings_application_questions_path, toggle_application_required_admin_settings_admins_path, admin_settings_test_connection_path, admin_settings_poll_click_path } from '../../../lib/routes';
export default function Admins({ admins, application_required, env_keys, app_urls, referral_test }) {
  const [showAddModal, setShowAddModal] = useState(false);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('admin');
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [togglingApplication, setTogglingApplication] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResults, setTestResults] = useState(null);
  const [referralStatus, setReferralStatus] = useState(null); // null | 'waiting' | 'success' | 'timeout'
  const pollRef = useRef(null);

  useEffect(() => {
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  function handleTestReferral() {
    if (!referral_test) return;
    setReferralStatus('waiting');
    window.open(referral_test.url, '_blank');

    const startedAt = Date.now();
    pollRef.current = setInterval(async () => {
      try {
        const res = await fetch(`${admin_settings_poll_click_path()}?slug=${referral_test.slug}`);
        const data = await res.json();
        if (data.received) {
          clearInterval(pollRef.current);
          setReferralStatus('success');
        } else if (Date.now() - startedAt > 30000) {
          clearInterval(pollRef.current);
          setReferralStatus('timeout');
        }
      } catch {
        // keep polling
      }
    }, 2000);
  }

  async function handleTestConnection() {
    setTesting(true);
    setTestResults(null);
    try {
      const res = await fetch(admin_settings_test_connection_path(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content,
        },
      });
      const data = await res.json();
      setTestResults(data);
    } catch {
      setTestResults({ success: false, results: { error: { success: false, message: 'Network error. Please try again.' } } });
    } finally {
      setTesting(false);
    }
  }

  function handleToggleApplication() {
    setTogglingApplication(true);
    router.post(toggle_application_required_admin_settings_admins_path(), {}, {
      onFinish: () => setTogglingApplication(false),
    });
  }
  function handleAdd() {
    setSubmitting(true);
    setErrors({});
    router.post(
      admin_settings_admins_path(),
      {
        admin: {
          email,
          role,
        },
      },
      {
        onSuccess: () => {
          setShowAddModal(false);
          setEmail('');
          setRole('admin');
          setSubmitting(false);
        },
        onError: (errs) => {
          setErrors(errs);
          setSubmitting(false);
        },
      },
    );
  }
  function handleDelete(admin) {
    router.delete(admin_settings_admin_path(admin.id), {
      onSuccess: () => setDeleteTarget(null),
    });
  }
  const columns = [
    {
      id: 'email',
      header: 'Email',
      sortable: true,
      render: (row) => <span className="font-medium text-fg">{row.email}</span>,
    },
    {
      id: 'role',
      header: 'Role',
      render: (row) => (
        <Badge variant={row.role === 'super_admin' ? 'info' : 'default'} size="sm">
          {row.role === 'super_admin' ? 'Super Admin' : 'Admin'}
        </Badge>
      ),
    },
    {
      id: 'actions',
      header: '',
      render: (row) => (
        <Button variant="ghost" size="sm" onClick={() => setDeleteTarget(row)} className="text-danger-600 hover:text-danger-700">
          Remove
        </Button>
      ),
    },
  ];
  return (
    <SettingsLayout>
      <PageHeader
        title="Admin Management"
        description="Manage admin users who can access the admin dashboard"
        actions={<Button onClick={() => setShowAddModal(true)}>Add Admin</Button>}
      />

      <Card className="mt-6">
        <CardHeader>
          <h3 className="text-base font-semibold text-fg">Program Settings</h3>
        </CardHeader>
        <CardBody>
          <Toggle
            label="Require Application"
            description="When enabled, new affiliates must fill out an application and be approved before accessing their dashboard. When disabled, new affiliates are auto-approved immediately."
            checked={application_required}
            onChange={handleToggleApplication}
            disabled={togglingApplication}
          />
          {application_required && (
            <Link
              href={admin_settings_application_questions_path()}
              className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-accent hover:text-accent-emphasis transition-colors"
            >
              Manage application questions
              <ArrowRight size={14} strokeWidth={2} />
            </Link>
          )}
        </CardBody>
      </Card>

      {app_urls && (
        <Card className="mt-6">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Globe size={18} strokeWidth={1.5} className="text-fg-secondary" />
              <h3 className="text-base font-semibold text-fg">App URLs</h3>
            </div>
            <p className="mt-1 text-sm text-fg-muted">
              Resolved URLs for the main app and this affiliates app.
            </p>
          </CardHeader>
          <CardBody>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-fg">Main App</span>
                <code className="text-sm text-fg-secondary">{app_urls.main}</code>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-fg">Affiliates App</span>
                <code className="text-sm text-fg-secondary">{app_urls.affiliates}</code>
              </div>
            </div>
          </CardBody>
        </Card>
      )}

      {env_keys && (
        <Collapsible title="Environment Variables" className="mt-6">
          <div className="space-y-2">
            {env_keys.map((key) => (
              <div key={key.name} className="flex items-center justify-between py-1.5">
                <code className="text-sm text-fg-secondary">{key.name}</code>
                {key.present ? (
                  <span className="flex items-center gap-1.5 text-sm text-success-600">
                    <CheckCircle size={14} />
                    Set
                  </span>
                ) : key.optional ? (
                  <span className="text-sm text-fg-tertiary">
                    Not set (optional)
                  </span>
                ) : (
                  <span className="flex items-center gap-1.5 text-sm text-danger-600">
                    <XCircle size={14} />
                    Missing
                  </span>
                )}
              </div>
            ))}
          </div>
        </Collapsible>
      )}

      <Card className="mt-6">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Wifi size={18} strokeWidth={1.5} className="text-fg-secondary" />
            <h3 className="text-base font-semibold text-fg">Connection Test</h3>
          </div>
          <p className="mt-1 text-sm text-fg-muted">
            Verify connectivity to the main app and inbound API endpoints.
          </p>
        </CardHeader>
        <CardBody>
          <div className="space-y-4">
            <Button
              variant="outline"
              onClick={handleTestConnection}
              loading={testing}
            >
              Run Test
            </Button>

            {testResults && (
              <div className="space-y-4">
                {/* Main App checks */}
                {(testResults.results.main_app || testResults.results.tracking_tag) && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-fg-muted">
                      <Server size={14} strokeWidth={1.5} />
                      Main App
                    </div>
                    {testResults.results.main_app && (
                      <div className="flex items-center gap-2 text-sm">
                        {testResults.results.main_app.success ? (
                          <CheckCircle size={16} className="shrink-0 text-success-600" />
                        ) : (
                          <XCircle size={16} className="shrink-0 text-danger-600" />
                        )}
                        <span className="text-fg-muted">{testResults.results.main_app.message}</span>
                      </div>
                    )}
                    {testResults.results.tracking_tag && (
                      <div className="flex items-center gap-2 text-sm">
                        {testResults.results.tracking_tag.success ? (
                          <CheckCircle size={16} className="shrink-0 text-success-600" />
                        ) : (
                          <XCircle size={16} className="shrink-0 text-danger-600" />
                        )}
                        <span className="text-fg-muted">{testResults.results.tracking_tag.message}</span>
                      </div>
                    )}
                  </div>
                )}

                {/* Inbound endpoint tests */}
                <div className="space-y-2">
                  <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-fg-muted">
                    <Wifi size={14} strokeWidth={1.5} />
                    Inbound Endpoints
                  </div>
                  {Object.entries(testResults.results)
                    .filter(([name]) => name !== 'main_app' && name !== 'tracking_tag')
                    .map(([name, result]) => (
                      <div key={name} className="flex items-center gap-2 text-sm">
                        {result.success ? (
                          <CheckCircle size={16} className="shrink-0 text-success-600" />
                        ) : (
                          <XCircle size={16} className="shrink-0 text-danger-600" />
                        )}
                        <span className="font-medium capitalize text-fg">{name}</span>
                        <span className="text-fg-muted">{result.message}</span>
                      </div>
                    ))}
                </div>
              </div>
            )}
          </div>
        </CardBody>
      </Card>

      {referral_test && (
        <Card className="mt-6">
          <CardHeader>
            <div className="flex items-center gap-2">
              <ExternalLink size={18} strokeWidth={1.5} className="text-fg-secondary" />
              <h3 className="text-base font-semibold text-fg">Referral Link Test</h3>
            </div>
            <p className="mt-1 text-sm text-fg-muted">
              Open a referral link on the main app and verify the click is tracked end-to-end.
            </p>
          </CardHeader>
          <CardBody>
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm">
                <span className="text-fg-muted">Test URL:</span>
                <code className="text-sm text-fg-secondary">{referral_test.url}</code>
              </div>
              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  onClick={handleTestReferral}
                  disabled={referralStatus === 'waiting'}
                >
                  {referralStatus === 'waiting' ? 'Waiting for click...' : 'Open & Listen'}
                </Button>
                {referralStatus === 'waiting' && (
                  <span className="flex items-center gap-1.5 text-sm text-fg-muted">
                    <Loader2 size={14} className="animate-spin" />
                    Listening for click (30s)...
                  </span>
                )}
                {referralStatus === 'success' && (
                  <span className="flex items-center gap-1.5 text-sm text-success-600">
                    <CheckCircle size={16} />
                    Click received — referral tracking is working
                  </span>
                )}
                {referralStatus === 'timeout' && (
                  <span className="flex items-center gap-1.5 text-sm text-danger-600">
                    <XCircle size={16} />
                    No click received — check that the tracking tag is installed
                  </span>
                )}
              </div>
            </div>
          </CardBody>
        </Card>
      )}

      <div className="mt-6">
        <DataTable
          columns={columns}
          data={admins}
          keyAccessor="id"
          emptyTitle="No admins"
          emptyDescription="Add your first admin user."
        />
      </div>

      {/* Add Admin Modal */}
      <Modal open={showAddModal} onClose={() => setShowAddModal(false)} size="sm">
        <ModalTitle>Add Admin</ModalTitle>
        <ModalBody>
          <div className="space-y-4">
            <Input
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              error={errors.email}
              placeholder="admin@example.com"
            />
            <Select label="Role" value={role} onChange={(e) => setRole(e.target.value)}>
              <option value="admin">Admin</option>
              <option value="super_admin">Super Admin</option>
            </Select>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button variant="secondary" onClick={() => setShowAddModal(false)}>
            Cancel
          </Button>
          <Button onClick={handleAdd} loading={submitting} disabled={!email.trim()}>
            Add Admin
          </Button>
        </ModalFooter>
      </Modal>

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => deleteTarget && handleDelete(deleteTarget)}
        title="Remove Admin"
        message={`Are you sure you want to remove ${deleteTarget?.email}? They will no longer be able to access the admin dashboard.`}
        confirmLabel="Remove"
        destructive
      />
    </SettingsLayout>
  );
}
